/**
 * FOI-538 — Product quiz scoring rules (model v2.2).
 *
 * Transcribed 2026-08-22 from the client's "Foil Drive Quiz Logic v2.2" sheet,
 * id 1Pl7D2gwJNVO2RrkdpeFrIYGPJmwnyvkzEH1VyzHep4Q, tabs 2–9.
 * CSV capture date: 2026-08-22. The sheet is the source of truth for these
 * numbers. A rules change ships as: edit sheet → update this file → `npm test`
 * → deploy.
 * Tested by tests/quiz-engine.test.js against the sheet's own Test & Preview
 * calculation (tests/fixtures/v22-test-preview.json).
 *
 * MOTOR BLANK-BLOCKS FLAG
 * The sheet's Motor tab has two fully blank question blocks. Its Status column
 * marks both: "⚠ NO WEIGHTS — fill this block or set importance to 0".
 * They are Q1 ability (sheet importance 2.0) and Q3 assistance (sheet
 * importance 2.0). Decision: set importance to 0, so both keys are omitted
 * from motor.importance below. Motor's denominator is therefore 8.5, not the
 * sheet's 12.5, and every motor score is the sheet's value × 12.5 / 8.5
 * (≈ ×1.4706). Ranking is identical — the blank blocks scored 0 for every
 * motor anyway — only the displayed match % rises.
 * When Luke fills the Motor Q1/Q3 cells: restore `ability: 2, assistance: 2`
 * to motor.importance and add the two weight blocks. That edit breaks 8 tests
 * across FOUR paths on purpose. All four must ship in the same release. The
 * 8 failures land in three test FILES: tests/quiz-rules.test.js (2),
 * tests/quiz-rules-csv.test.js (2) and tests/quiz-engine.test.js (4). The
 * fixture below is data, not a test file — its 4 failures are reported by
 * tests/quiz-engine.test.js, which is the one that is easy to miss because
 * nothing in it mentions importance at all.
 *
 *   1. tests/quiz-rules.test.js
 *      - "sheet importance totals produce the share denominators" pins the
 *        motor total at 8.5. It becomes 12.5.
 *      - "every scored question has a weights block behind it" fails until the
 *        two new weight blocks are actually written.
 *   2. tests/fixtures/v22-test-preview.json, read by tests/quiz-engine.test.js
 *      - every motor score in every case. They currently carry the ×12.5/8.5
 *        rescale; recompute them without it, and update `motorRescaleNote`.
 *        The 4 red tests are in tests/quiz-engine.test.js — fixing the JSON is
 *        what turns them green, so fix the JSON, do not touch the assertions.
 *   3. tests/quiz-rules-csv.test.js  ← the other one that is easy to miss
 *      - DELETE both entries in IMPORTANCE_OVERRIDES (`motor: { ability: 0,
 *        assistance: 0 }`, near the top of that file). Leaving them there makes
 *        "CSV oracle: every importance cell matches tab 2" fail, because the
 *        override still says 0 while the code and the sheet both say 2.
 *      - Re-export tests/fixtures/v22-csv/motor.csv from the sheet and update
 *        its SHA-256 in tests/fixtures/v22-csv/SOURCE.md. Without the fresh
 *        export the new weight cells are compared against the old blank ones.
 *      - "CSV oracle: every weight cell is covered, matched or pinned blank"
 *        pins the split as 440 matched / 140 blank. The two motor blocks move
 *        from blank to matched: the counts become 464 and 116.
 *
 * TIE-BREAK
 * Equal scores keep the product order of the `products` array in this file
 * (the engine's sort is order-preserving). The sheet does not define a
 * tie-break rule; this is the engine's deterministic choice.
 *
 * Weight scale: 0–1 per answer × product. Blank cell in the sheet = absent key
 * here = 0. A fully blank answer row is written as an explicit `{}` so the
 * row-coverage guard in tests/quiz-rules.test.js still sees the row: it means
 * "sheet row read, every cell blank", not "row forgotten".
 *
 * score(product, component) =
 *   Σ over questions ( importance / Σ importances × weight(answer, product) )
 */
(function () {
  'use strict';

  var PD_QUIZ_RULES = {
    version: '2026-08-22',

    // Stage order = design order = sheet order (Figma 5567:1584).
    //
    // THE `label` AND `stageLabel` STRINGS BELOW ARE NOT RENDERED ANYWHERE.
    // Every word a shopper reads lives in src/snippets/pd-quiz.liquid, which
    // FOI-537 rewrote from the client's COPY tab. The engine reads only `key`,
    // `type` and `max`. These strings are stale on purpose: keeping a second
    // copy of the wording in sync is a maintenance trap, and a wrong label here
    // cannot reach a customer. Read the Liquid for the real text.
    //
    // The `key` values ARE the scoring contract. They must match the
    // `option_keys` lists in the Liquid exactly. Never rename one.
    questions: [
      {
        key: 'ability',
        stageLabel: 'ABILITY',
        label: 'What level are you?',
        type: 'single',
        options: [
          { key: 'first_timer', label: 'First Timer' },
          { key: 'beginner', label: 'Novice' },
          { key: 'intermediate', label: 'Intermediate' },
          { key: 'advanced', label: 'Advanced' },
          { key: 'professional', label: 'Professional' }
        ]
      },
      {
        key: 'discipline',
        stageLabel: 'DISCIPLINE',
        label: "What's your mission?",
        type: 'multi',
        max: 3,
        options: [
          // Sheet row "Efoil / Flatwater Leisure"; design copy "Efoil / Cruise".
          { key: 'efoil_cruise', label: 'Efoil / Cruise' },
          { key: 'flatwater_pump', label: 'Flatwater Pump' },
          { key: 'surf_foil', label: 'Surf Foil' },
          { key: 'downwind_foil', label: 'Downwind Foil' },
          { key: 'wing_parawing', label: 'Wing / Parawing' },
          { key: 'wake_foil', label: 'Wake Foil' },
          { key: 'not_sure', label: 'Not Sure Yet' }
        ]
      },
      {
        key: 'assistance',
        stageLabel: 'ASSISTANCE',
        label: 'How much motor?',
        type: 'single',
        options: [
          { key: 'efoil_full', label: 'Efoil – full reliance on motor' },
          { key: 'assist_heavy', label: 'Assist – heavy motor use' },
          { key: 'assist_light', label: 'Assist – light motor use' }
        ]
      },
      {
        key: 'weight',
        stageLabel: 'WEIGHT',
        // Scoring keys stay w50_75 / w75_90, following the weighting tabs.
        // The COPY tab shows the bands as 50-70 / 70-90; that is on-screen
        // wording only and does not move the score band (decision D2).
        label: "What's your weight?",
        type: 'single',
        options: [
          { key: 'under_50', label: '<50kg' },
          { key: 'w50_75', label: '50–75kg' },
          { key: 'w75_90', label: '75–90kg' },
          { key: 'w90_plus', label: '90kg+' }
        ]
      },
      {
        key: 'priority',
        stageLabel: 'PRIORITY',
        label: 'Agility or power?',
        type: 'single',
        options: [
          { key: 'lightweight', label: 'Lightweight & agile' },
          { key: 'power', label: 'Power & runtime' },
          { key: 'balance', label: 'A balance of both' }
        ]
      },
      {
        key: 'budget',
        stageLabel: 'BUDGET',
        label: "What's your budget?",
        type: 'single',
        options: [
          { key: 'best', label: 'Best match' },
          { key: 'premium', label: 'Premium but sensible' },
          { key: 'just_enough', label: 'Just enough to start' },
          { key: 'bare_bones', label: 'Bare bones entry level' }
        ]
      },
      {
        // v2.2: mast is SCORED — importance 3.0 on motor and cable (sheet
        // tab 2). It is no longer a plain switch.
        key: 'mast',
        stageLabel: 'MAST',
        label: 'What type of mast?',
        type: 'single',
        options: [
          { key: 'standard', label: 'Retro-Fit Mast (Removable Motor)' },
          { key: 'integrated', label: 'Integrated Mast (Sold Separate)' },
          { key: 'not_sure', label: 'Not sure' }
        ]
      }
    ],

    // components[c].importance: sheet tab 2 (0–5 per question; the engine
    // normalises to shares). Questions with importance 0 are omitted.
    // components[c].weights[question][answer][product]: sheet tabs 3–7.
    components: {
      // Importance total 12.5. Sheet tab 3. Q7 mast is importance 0 → omitted.
      system: {
        products: ['MAX+', 'Slim', 'Fusion'],
        importance: { ability: 2, discipline: 1.5, assistance: 2.5, weight: 2.5, priority: 3, budget: 1 },
        weights: {
          ability: {
            first_timer: { 'MAX+': 0.9, Slim: 0, Fusion: 0.95 },
            beginner: { 'MAX+': 0.9, Slim: 0, Fusion: 0.95 },
            intermediate: { 'MAX+': 0.9, Slim: 0.1, Fusion: 0.9 },
            advanced: { 'MAX+': 0.9, Slim: 0.5, Fusion: 0.8 },
            professional: { 'MAX+': 0.9, Slim: 0.7, Fusion: 0.8 }
          },
          discipline: {
            efoil_cruise: { 'MAX+': 0.8, Slim: 0, Fusion: 1 },
            flatwater_pump: { 'MAX+': 0.9, Slim: 0.7, Fusion: 0.2 },
            surf_foil: { 'MAX+': 0.9, Slim: 0.5, Fusion: 0.7 },
            downwind_foil: { 'MAX+': 0.9, Slim: 0.5, Fusion: 0.6 },
            wing_parawing: { 'MAX+': 0.9, Slim: 0.5, Fusion: 0.6 },
            wake_foil: { 'MAX+': 0.9, Slim: 0.2, Fusion: 0.6 },
            not_sure: { 'MAX+': 0.6, Slim: 0.3, Fusion: 0.7 }
          },
          assistance: {
            efoil_full: { 'MAX+': 0.7, Slim: 0, Fusion: 1 },
            assist_heavy: { 'MAX+': 0.8, Slim: 0, Fusion: 0.9 },
            assist_light: { 'MAX+': 1, Slim: 0.6, Fusion: 0.8 }
          },
          weight: {
            under_50: { 'MAX+': 1, Slim: 0.6, Fusion: 0.3 },
            w50_75: { 'MAX+': 1, Slim: 0.6, Fusion: 0.8 },
            w75_90: { 'MAX+': 0.7, Slim: 0.5, Fusion: 1 },
            w90_plus: { 'MAX+': 0.7, Slim: 0, Fusion: 1 }
          },
          priority: {
            lightweight: { 'MAX+': 0.4, Slim: 1, Fusion: 0.1 },
            power: { 'MAX+': 0.7, Slim: 0, Fusion: 1 },
            balance: { 'MAX+': 1, Slim: 0, Fusion: 0.6 }
          },
          budget: {
            best: { 'MAX+': 1, Slim: 0.5, Fusion: 1 },
            premium: { 'MAX+': 1, Slim: 0.5, Fusion: 0.9 },
            just_enough: { 'MAX+': 1, Slim: 0.8, Fusion: 0.4 },
            bare_bones: { 'MAX+': 0.9, Slim: 0.8, Fusion: 0.3 }
          }
        }
      },

      // Importance total 9.5. Sheet tab 4. Q7 mast is importance 0 → omitted.
      battery: {
        products: ['Fusion 860', 'FM+ 650', 'FM+ 430', 'Slim Performance'],
        importance: { ability: 1, discipline: 0.5, assistance: 2.5, weight: 1.5, priority: 2, budget: 2 },
        weights: {
          ability: {
            first_timer: { 'Fusion 860': 0.9, 'FM+ 650': 1, 'FM+ 430': 0.7, 'Slim Performance': 0 },
            beginner: { 'Fusion 860': 0.9, 'FM+ 650': 1, 'FM+ 430': 0.7, 'Slim Performance': 0 },
            intermediate: { 'Fusion 860': 0.8, 'FM+ 650': 1, 'FM+ 430': 0.9, 'Slim Performance': 0.4 },
            advanced: { 'Fusion 860': 0.8, 'FM+ 650': 1, 'FM+ 430': 0.9, 'Slim Performance': 0.8 },
            professional: { 'Fusion 860': 0.8, 'FM+ 650': 1, 'FM+ 430': 1, 'Slim Performance': 0.8 }
          },
          discipline: {
            efoil_cruise: { 'Fusion 860': 1, 'FM+ 650': 1, 'FM+ 430': 0.6, 'Slim Performance': 0.2 },
            flatwater_pump: { 'Fusion 860': 0.6, 'FM+ 650': 0.6, 'FM+ 430': 0.85, 'Slim Performance': 1 },
            surf_foil: { 'Fusion 860': 0.7, 'FM+ 650': 0.6, 'FM+ 430': 0.85, 'Slim Performance': 1 },
            downwind_foil: { 'Fusion 860': 0.8, 'FM+ 650': 1, 'FM+ 430': 0.8, 'Slim Performance': 0.4 },
            wing_parawing: { 'Fusion 860': 0.6, 'FM+ 650': 0.85, 'FM+ 430': 0.9, 'Slim Performance': 0.7 },
            wake_foil: { 'Fusion 860': 0.6, 'FM+ 650': 0.7, 'FM+ 430': 0.9, 'Slim Performance': 0.9 },
            not_sure: { 'Fusion 860': 0.5, 'FM+ 650': 0.5, 'FM+ 430': 0.5, 'Slim Performance': 0.5 }
          },
          assistance: {
            efoil_full: { 'Fusion 860': 1, 'FM+ 650': 1, 'FM+ 430': 0.4, 'Slim Performance': 0 },
            assist_heavy: { 'Fusion 860': 0.8, 'FM+ 650': 1, 'FM+ 430': 0.8, 'Slim Performance': 0 },
            assist_light: { 'Fusion 860': 0.2, 'FM+ 650': 0.8, 'FM+ 430': 1, 'Slim Performance': 0.8 }
          },
          weight: {
            under_50: { 'Fusion 860': 0.3, 'FM+ 650': 0.7, 'FM+ 430': 0.8, 'Slim Performance': 1 },
            w50_75: { 'Fusion 860': 0.8, 'FM+ 650': 1, 'FM+ 430': 0.9, 'Slim Performance': 0.9 },
            w75_90: { 'Fusion 860': 0.9, 'FM+ 650': 0.9, 'FM+ 430': 0.8, 'Slim Performance': 0.6 },
            w90_plus: { 'Fusion 860': 1, 'FM+ 650': 0.9, 'FM+ 430': 0.7, 'Slim Performance': 0.2 }
          },
          priority: {
            lightweight: { 'Fusion 860': 0.1, 'FM+ 650': 0.3, 'FM+ 430': 0.7, 'Slim Performance': 1 },
            power: { 'Fusion 860': 1, 'FM+ 650': 0.5, 'FM+ 430': 0.3, 'Slim Performance': 0.1 },
            balance: { 'Fusion 860': 0.4, 'FM+ 650': 1, 'FM+ 430': 0.9, 'Slim Performance': 0.3 }
          },
          budget: {
            best: { 'Fusion 860': 1, 'FM+ 650': 1, 'FM+ 430': 0.6, 'Slim Performance': 0.6 },
            premium: { 'Fusion 860': 0.9, 'FM+ 650': 1, 'FM+ 430': 0.8, 'Slim Performance': 0.9 },
            just_enough: { 'Fusion 860': 0.4, 'FM+ 650': 0.7, 'FM+ 430': 1, 'Slim Performance': 0.8 },
            bare_bones: { 'Fusion 860': 0.4, 'FM+ 650': 1, 'FM+ 430': 0.8, 'Slim Performance': 0.7 }
          }
        }
      },

      // Importance total 8.5. Sheet tab 5.
      // ability + assistance omitted — see MOTOR BLANK-BLOCKS FLAG at the top.
      // The `{}` rows below are sheet rows read as fully blank, on purpose.
      motor: {
        products: ['Retro-Fit HP Motor Bundle', 'Integrated Mast Motor Bundle', 'Retro-Fit Standard Motor Bundle'],
        importance: { discipline: 0.5, weight: 2, priority: 2, budget: 1, mast: 3 },
        weights: {
          discipline: {
            efoil_cruise: { 'Retro-Fit HP Motor Bundle': 1, 'Integrated Mast Motor Bundle': 1 },
            flatwater_pump: {},
            surf_foil: {},
            downwind_foil: {},
            wing_parawing: {},
            wake_foil: {},
            not_sure: {}
          },
          weight: {
            under_50: { 'Retro-Fit HP Motor Bundle': 1, 'Integrated Mast Motor Bundle': 1, 'Retro-Fit Standard Motor Bundle': 1 },
            w50_75: { 'Retro-Fit HP Motor Bundle': 1, 'Integrated Mast Motor Bundle': 1, 'Retro-Fit Standard Motor Bundle': 1 },
            w75_90: { 'Retro-Fit HP Motor Bundle': 1, 'Integrated Mast Motor Bundle': 1 },
            w90_plus: { 'Retro-Fit HP Motor Bundle': 1, 'Integrated Mast Motor Bundle': 1 }
          },
          priority: {
            lightweight: {},
            power: { 'Retro-Fit HP Motor Bundle': 1, 'Integrated Mast Motor Bundle': 1 },
            balance: {}
          },
          budget: {
            best: { 'Retro-Fit HP Motor Bundle': 0.9, 'Integrated Mast Motor Bundle': 1 },
            premium: { 'Retro-Fit HP Motor Bundle': 1, 'Integrated Mast Motor Bundle': 0.9 },
            just_enough: { 'Retro-Fit HP Motor Bundle': 1, 'Integrated Mast Motor Bundle': 0.7, 'Retro-Fit Standard Motor Bundle': 0.8 },
            bare_bones: { 'Retro-Fit HP Motor Bundle': 0.8, 'Integrated Mast Motor Bundle': 0.6, 'Retro-Fit Standard Motor Bundle': 1 }
          },
          // The mast block is what makes the integrated motor win the
          // integrated path without any hardcoded swap in the engine.
          mast: {
            standard: { 'Retro-Fit HP Motor Bundle': 1, 'Retro-Fit Standard Motor Bundle': 1 },
            integrated: { 'Integrated Mast Motor Bundle': 1 },
            not_sure: { 'Retro-Fit HP Motor Bundle': 0.5, 'Integrated Mast Motor Bundle': 0.5, 'Retro-Fit Standard Motor Bundle': 0.5 }
          }
        }
      },

      // Importance total 9. Sheet tab 6. Q4/Q5/Q6 are importance 0 → omitted.
      // These sizes are motor HEIGHTS; on the Retro-Fit path a height can also
      // be a cable to buy. See `includedInKit.cable` and `fusionCableLookup`.
      cable: {
        products: ['12', '15', '20', '25', '30', '60', '70'],
        importance: { ability: 2, discipline: 2.5, assistance: 1.5, mast: 3 },
        weights: {
          ability: {
            first_timer: { 12: 0.1, 15: 0.48, 20: 0.9, 25: 1, 30: 0.37, 60: 0.9, 70: 0.9 },
            beginner: { 12: 0.12, 15: 0.52, 20: 0.9, 25: 1, 30: 0.31, 60: 0.9, 70: 0.9 },
            intermediate: { 12: 0.25, 15: 0.71, 20: 1, 25: 0.71, 30: 0.25, 60: 0.9, 70: 0.9 },
            advanced: { 12: 1, 15: 0.8, 20: 0.21, 60: 0.9, 70: 0.9 },
            professional: { 12: 1, 15: 0.68, 20: 0.15, 60: 0.9, 70: 0.9 }
          },
          discipline: {
            efoil_cruise: { 60: 0.78, 70: 1 },
            flatwater_pump: { 12: 0.25, 15: 0.82, 20: 0.9, 25: 0.35, 30: 0.07 },
            surf_foil: { 12: 0.25, 15: 1, 20: 0.82, 25: 0.25 },
            downwind_foil: { 12: 1, 15: 0.81, 20: 0.17, 25: 0.25 },
            wing_parawing: { 12: 0.25, 15: 0.25, 20: 0.82, 25: 1, 30: 0.45 },
            wake_foil: { 15: 0.12, 20: 0.55, 25: 1, 30: 0.73 },
            not_sure: { 12: 0.5, 15: 0.84, 20: 1, 25: 0.84, 30: 0.5, 60: 0.21, 70: 0.21 }
          },
          assistance: {
            efoil_full: { 60: 1, 70: 1 },
            assist_heavy: { 15: 0.8, 20: 1, 25: 0.6, 30: 0.3 },
            assist_light: { 12: 0.25, 15: 0.8, 20: 1, 25: 0.25 }
          },
          mast: {
            standard: { 12: 1, 15: 1, 20: 1, 25: 1, 30: 1, 60: 1, 70: 1 },
            integrated: { 12: 0, 15: 0, 20: 0, 25: 0, 30: 0, 60: 0, 70: 0 },
            not_sure: { 12: 0.8, 15: 0.8, 20: 0.8, 25: 0.8, 30: 0.8, 60: 0.8, 70: 0.8 }
          }
        }
      },

      // Importance total 5.5. Sheet tab 7. Q5/Q7 are importance 0 → omitted.
      accessory: {
        products: ['6% Upgrade', '10% Upgrade', 'Propeller Guard'],
        importance: { ability: 1.5, discipline: 0.5, assistance: 1, weight: 1, budget: 1.5 },
        weights: {
          ability: {
            first_timer: { '6% Upgrade': 0, '10% Upgrade': 0, 'Propeller Guard': 0.9 },
            beginner: { '6% Upgrade': 0.15, '10% Upgrade': 0.05, 'Propeller Guard': 0.6 },
            intermediate: { '6% Upgrade': 0.4, '10% Upgrade': 0.3, 'Propeller Guard': 0.25 },
            advanced: { '6% Upgrade': 0.5, '10% Upgrade': 1, 'Propeller Guard': 0.1 },
            professional: { '6% Upgrade': 0.5, '10% Upgrade': 1, 'Propeller Guard': 0.1 }
          },
          discipline: {
            efoil_cruise: { '6% Upgrade': 0.9, '10% Upgrade': 0.7, 'Propeller Guard': 0.7 },
            flatwater_pump: { '6% Upgrade': 0.9, '10% Upgrade': 0.4, 'Propeller Guard': 0.2 },
            surf_foil: { '6% Upgrade': 0.9, '10% Upgrade': 0.5, 'Propeller Guard': 0.15 },
            downwind_foil: { '6% Upgrade': 0.8, '10% Upgrade': 1, 'Propeller Guard': 0.1 },
            wing_parawing: { '6% Upgrade': 0.9, '10% Upgrade': 0.6, 'Propeller Guard': 0.15 },
            wake_foil: { '6% Upgrade': 0.8, '10% Upgrade': 0.4, 'Propeller Guard': 0.15 },
            not_sure: { '6% Upgrade': 0.5, '10% Upgrade': 0.5, 'Propeller Guard': 0.5 }
          },
          assistance: {
            efoil_full: { '6% Upgrade': 0.45, '10% Upgrade': 0.45, 'Propeller Guard': 0.8 },
            assist_heavy: { '6% Upgrade': 0.7, '10% Upgrade': 0.4, 'Propeller Guard': 0.2 },
            assist_light: { '6% Upgrade': 0.4, '10% Upgrade': 0.5, 'Propeller Guard': 0.2 }
          },
          weight: {
            under_50: { '6% Upgrade': 0.15, '10% Upgrade': 0.4, 'Propeller Guard': 0.3 },
            w50_75: { '6% Upgrade': 0.35, '10% Upgrade': 0.4, 'Propeller Guard': 0.3 },
            w75_90: { '6% Upgrade': 0.9, '10% Upgrade': 0.4, 'Propeller Guard': 0.2 },
            w90_plus: { '6% Upgrade': 1, '10% Upgrade': 0.35, 'Propeller Guard': 0.2 }
          },
          budget: {
            best: { '6% Upgrade': 0.8, '10% Upgrade': 0.7, 'Propeller Guard': 0.5 },
            premium: { '6% Upgrade': 0.6, '10% Upgrade': 0.6, 'Propeller Guard': 0.5 },
            // Sheet leaves both prop upgrades blank on the two cheap budgets.
            just_enough: { 'Propeller Guard': 0.5 },
            bare_bones: { 'Propeller Guard': 0.5 }
          }
        }
      }
    },

    // Sheet tab 8. 1 = compatible, 0 = not. Applied AFTER the winning system
    // is chosen: incompatible batteries/motors are forced to score 0 before
    // ranking, so the build is always physically possible.
    compatibility: {
      battery: {
        'Fusion 860': { 'MAX+': 0, Slim: 0, Fusion: 1 },
        // ── FM+ 430 DECEMBER LAUNCH FLAG ──────────────────────────────
        // Sheet tab 8: "PRE-LAUNCH — set to 1/0/1 when the FM+ 430 goes
        // live (Dec)". This row is the ONLY engine-side switch: change it to
        // { 'MAX+': 1, Slim: 0, Fusion: 1 } and add the new product's handle
        // in pd-quiz-config.liquid.
        // The data edit is one line, but the RELEASE is not. That edit breaks
        // 10 tests across FIVE paths, all on purpose. All five ship together:
        //   1. tests/quiz-rules.test.js      (the all-zero row assertion)
        //   2. tests/quiz-engine.test.js     (7 tests: the never-recommended
        //      case, three sheet fixtures, and the out-of-stock fallback cases
        //      that rely on FM+ 430 being blocked)
        //   3. tests/quiz-sweep.test.js      (the sweep-wide assertion)
        //   4. tests/fixtures/v22-test-preview.json  (cases "anchor",
        //      "derived 5" and "derived 6" — NOT just the obvious one.
        //      "anchor" pins scores.battery["FM+ 430"] at 0, and "derived 5"
        //      expects FM+ 650 where the FM+ 430 would then outscore it.
        //      tests/quiz-flag-checklists.test.js derives this list by
        //      flipping the row and re-running every case, so it cannot go
        //      stale again.)
        //   5. tests/quiz-rules-csv.test.js  ← the one that is easy to miss.
        //      "CSV oracle: the compatibility grid matches tab 8 exactly"
        //      fails. DO NOT delete that assertion — it is the only thing
        //      checking these numbers against the client's sheet at all, and
        //      dropping it throws the whole CSV-oracle protection away. The
        //      fix is a data refresh, not a test edit: re-export
        //      tests/fixtures/v22-csv/compatibility.csv from tab 8 (gid
        //      14645155) once Luke has flipped the row IN THE SHEET, then
        //      update its SHA-256 and the export date in
        //      tests/fixtures/v22-csv/SOURCE.md. If the sheet still says
        //      0/0/0, the code is not allowed to say anything else.
        'FM+ 430': { 'MAX+': 0, Slim: 0, Fusion: 0 },
        'FM+ 650': { 'MAX+': 1, Slim: 0, Fusion: 1 },
        'Slim Performance': { 'MAX+': 0, Slim: 1, Fusion: 0 }
      },
      // All 1s today — it filters nothing, but the grid stays so a future
      // sheet edit has a place to land.
      motor: {
        'Retro-Fit HP Motor Bundle': { 'MAX+': 1, Slim: 1, Fusion: 1 },
        'Integrated Mast Motor Bundle': { 'MAX+': 1, Slim: 1, Fusion: 1 },
        'Retro-Fit Standard Motor Bundle': { 'MAX+': 1, Slim: 1, Fusion: 1 }
      }
    },

    // The engine reads this instead of hardcoding the integrated motor name.
    // A result whose winning motor is this key is an Integrated Mast build:
    // no cable to buy, motor height reported instead.
    integratedMotorKey: 'Integrated Mast Motor Bundle',

    // Sheet tab 10 "ACCESSORY SELECTION LOGIC": 3 SKUs collapse into 2
    // mutually-exclusive slots before ranking. Max 2 recommended.
    // Blade hubs are gone from the model — the motor ships with its own prop.
    accessorySlots: {
      propUpgrade: ['6% Upgrade', '10% Upgrade'],
      guard: ['Propeller Guard']
    },
    // Only accessories AT or above this score are recommended (sheet tab 10
    // "MATCH THRESHOLD" cell value 0.4). 0.4 exactly passes.
    accessoryThreshold: 0.4,
    // The 20cm cable ships with every Retro-Fit motor bundle: shown as
    // "included", never sold again. The Integrated Mast build has no cable.
    includedInKit: { cable: '20' },

    // Dual-cable rule (sheet tab 10 "INTERCHANGEABLE CABLE CHECK"):
    // disciplines spanning both groups → recommend BOTH cables.
    cableGroups: {
      short: ['flatwater_pump', 'surf_foil', 'downwind_foil', 'wing_parawing', 'wake_foil'],
      long: ['efoil_cruise']
    },
    // Which sizes serve each group ("the long 60/70cm cable is an add-on kept
    // alongside" — sheet tab 10).
    cableSizeGroups: {
      short: ['12', '15', '20', '25', '30'],
      long: ['60', '70']
    },
    // Fusion Gen2.5 plug sits ~5cm further from the mast: Fusion riders order
    // one step longer (sheet tab 10 "FUSION CABLE ADJUSTMENT" lookup).
    fusionCableLookup: { 12: '15', 15: '20', 20: '25', 25: '30', 30: '30', 60: '60', 70: '70' },

    // Sheet tab 9 — internal key → customer-facing display name.
    displayNames: {
      'MAX+': 'Assist MAX+',
      Slim: 'Assist Slim',
      Fusion: 'Fusion',
      'Fusion 860': 'Fusion 860 Battery',
      'FM+ 650': 'FM+ 650 Battery',
      'FM+ 430': 'FM+ 430 Battery',
      'Slim Performance': 'Assist Slim Performance',
      'Retro-Fit HP Motor Bundle': 'Retro-Fit HP Motor Bundle',
      'Integrated Mast Motor Bundle': 'Integrated Mast Motor Bundle',
      'Retro-Fit Standard Motor Bundle': 'Retro-Fit Standard Motor Bundle',
      12: '12cm Motor Height',
      15: '15cm Motor Height',
      20: '20cm Motor Height',
      25: '25cm Motor Height',
      30: '30cm Motor Height',
      60: '60cm Motor Height',
      70: '70cm Motor Height',
      '6% Upgrade': 'Performance Low Range 6 Props',
      '10% Upgrade': 'Performance Mid-High Range 10 Props',
      'Propeller Guard': 'Propeller Guard'
    }
  };

  if (typeof window !== 'undefined') {
    window.PD_QUIZ_RULES = PD_QUIZ_RULES;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = PD_QUIZ_RULES;
  }
})();
